import React, { useState, useEffect, useMemo, type ReactElement, type RefCallback } from 'react';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import type {
	DropdownTriggerParams,
	CreatePullRequestTarget,
} from '@atlassian/jira-development-summary-common/src/common/types.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { createPullRequestDropdownContentEntrypoint } from './create-pull-request-dropdown-content/entrypoint.tsx';

export type Props = {
	hasErrors: boolean;
	renderDropdownTrigger: (props: DropdownTriggerParams) => ReactElement;
	createPullRequestTargets: CreatePullRequestTarget[] | null | undefined;
	onAuthErrorClick?: () => void;
	onOpenDropdown?: (arg1: { isOpen: boolean }) => void;
};
export const useIsDropdownOpen = (isOpen = false) => useState(isOpen);

const entryPointParams = {};

const CreatePullRequestDropdown = ({
	createPullRequestTargets,
	renderDropdownTrigger,
	onOpenDropdown,
	hasErrors,
	onAuthErrorClick,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useIsDropdownOpen();

	const wasOpen = usePreviousWithInitial(isOpen);

	useEffect(() => {
		if (wasOpen !== isOpen) {
			isOpen &&
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown opened', 'createPullRequestDropdown');
			!isOpen &&
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown closed', 'createPullRequestDropdown');
			onOpenDropdown && onOpenDropdown({ isOpen });
		}
	}, [createAnalyticsEvent, wasOpen, isOpen, onOpenDropdown]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		createPullRequestDropdownContentEntrypoint,
		entryPointParams,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const runtimeProps = useMemo(
		() => ({
			createPullRequestTargets,
			hasErrors,
			onAuthErrorClick,
			setIsOpen,
		}),
		[hasErrors, createPullRequestTargets, onAuthErrorClick, setIsOpen],
	);

	return (
		<Popup
			id="create-pull-request-dropdown"
			testId="development-summary-pull-request.ui.create-pull-request-summary-item.create-pull-request-dropdown.popup"
			messageId="development-summary-pull-request.ui.create-pull-request-summary-item.create-pull-request-dropdown.popup"
			messageType="transactional"
			zIndex={layers.modal + 1}
			placement="bottom-end"
			shouldFlip
			trigger={(triggerProps) =>
				renderDropdownTrigger({
					...triggerProps,
					ref: (element) => {
						triggerRef(element);
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(triggerProps.ref as RefCallback<HTMLElement>)(element);
					},
					isSelected: isOpen,
					onClick: () => setIsOpen(!isOpen),
				})
			}
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="dev-panel-create-pull-request-content"
					packageName="jira-development-summary-branch"
					fallback={null}
					runtimeProps={runtimeProps}
				/>
			)}
		/>
	);
};

export default CreatePullRequestDropdown;
